import React, { useState } from 'react';
import styled from 'styled-components/macro';
import {
  Button,
  Flex,
  Select,
  Title,
  Loader,
  Body,
  OptionType,
} from '@cognite/cogs.js';
import { Modal, notification } from 'antd';
import { useGetProjectQuery } from 'hooks/projects';
import {
  SchemaCollection,
  useUpdateSchemaCollectionRefIdMutation,
} from 'types/dt-graphql-apollo-v2';
import {
  getVersionSelectOptions,
  markOldVersion,
} from 'pages/ViewDigitalTwinPage/components/Schemas/helpers';
import { createRefId, parseRefId } from 'utils/misc';

interface Props {
  schemaCollection: SchemaCollection;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditSchemaVersionDialog = ({
  schemaCollection,
  onSuccess,
  onClose,
}: Props) => {
  const {
    data: { Project: projects },
    loading: loadingProject,
    error: errorFetchingProject,
  } = useGetProjectQuery(schemaCollection.refId);
  const [selectedVersion, setSelectedVersion] = useState(
    parseRefId(schemaCollection.refId).dataModelVersion
  );
  const versionOptions = getVersionSelectOptions(projects?.[0]?.versions || []);
  const selectedVersionOption = versionOptions.find(
    (versionOption) => versionOption.value === selectedVersion
  ) || { label: 'No versions', value: '1' };
  const [updateVersion, { loading: updatingVersion }] =
    useUpdateSchemaCollectionRefIdMutation();

  const onUpdate = async () => {
    const { dataModel, space } = parseRefId(schemaCollection.refId);
    const newRefId = createRefId(space, dataModel, selectedVersion);

    try {
      await updateVersion({
        variables: {
          schemaCollectionId: schemaCollection.externalId,
          refId: newRefId,
          name: schemaCollection.name,
          tenant: schemaCollection.tenant,
        },
      });
      notification.success({
        message: `Update ${schemaCollection.name} version to ${selectedVersionOption?.label}`,
      });
      onSuccess();
    } catch (error: any) {
      notification.error({
        message: `Failed to update schema version of ${schemaCollection.name}`,
        description: error?.message,
      });
    }
  };

  if (errorFetchingProject) {
    return <>Couldn't fetch the project</>;
  }
  if (loadingProject) {
    return <Loader />;
  }

  return (
    <Modal visible footer={null} onCancel={onClose}>
      <Title level={4} style={{ marginBottom: '1.5rem' }}>
        Edit schema version
      </Title>
      <Body>
        Select the schema version that you want to base your data validation on.
        Note that another schema version may have changes that affect the
        outcome of your validation.{' '}
      </Body>
      <Flex gap={10} alignItems="center">
        <b>schema version: </b>
        <SelectContainer>
          <Select
            placeholderSelectText="Schema versions"
            disableTyping
            value={markOldVersion(versionOptions, selectedVersionOption)}
            options={versionOptions}
            onChange={(option: OptionType<string>) =>
              setSelectedVersion(option.value || '')
            }
          />
        </SelectContainer>
      </Flex>
      <StyledButtons>
        <Button type="ghost" onClick={onClose} style={{ marginRight: '1rem' }}>
          Cancel
        </Button>
        <Button
          type="primary"
          loading={updatingVersion}
          onClick={(e) => {
            e.stopPropagation();
            onUpdate();
          }}
        >
          Save
        </Button>
      </StyledButtons>
    </Modal>
  );
};

const SelectContainer = styled.div`
  width: 150px;
`;

const StyledButtons = styled.div`
  padding: 2rem 0 0 0;
  text-align: right;
`;
